/* Page skeleton: the moment a link is followed, the next page's layout appears as placeholder blocks
   (snippets/page-skeleton.liquid) so a slow response shows structure instead of the page being left.
   (DELAY can hold it back so that fast responses skip it; the brand prefers it instant.)
   The cross-document view transition then fades the skeleton into the real content (html.from-skeleton,
   layout/theme.liquid); when there is no transition (Firefox, or the browser gave up on a slow load) the new page
   redraws the same skeleton and fades it out as its entrance starts. */
(() => {
  const host = document.querySelector('[data-page-skeleton]');
  if (!host) return;
  const root = document.documentElement;
  const rootUrl = (window.CJR?.routes?.root_url || '/').replace(/\/$/, '');
  const templates = {};
  document.querySelectorAll('template[data-skeleton-kind]').forEach((t) => { templates[t.dataset.skeletonKind] = t; });

  // Menu and footer links say what they lead to (snippets/link-kind.liquid); remember it per path for any link to the same page
  const known = new Map();
  document.querySelectorAll('a[href][data-skeleton]').forEach((a) => {
    try { known.set(new URL(a.href).pathname, a.dataset.skeleton); } catch (e) {}
  });

  /** Which layout the URL will render, or null for pages the skeleton can't draw. Page rules mirror sections/main-page.liquid. */
  const kindFor = (url, hint) => {
    if (hint && templates[hint]) return hint;
    let path = url.pathname;
    if (rootUrl && path.startsWith(rootUrl)) path = path.slice(rootUrl.length);
    path = path.replace(/\/+$/, '') || '/';
    if (known.has(path)) return known.get(path);
    if (path === '/') return 'index';
    if (/^\/(collections\/[^/]+\/)?products\/[^/]+$/.test(path)) return 'product';
    if (/^\/collections\/[^/]+/.test(path)) return 'collection';
    if (path === '/search') return 'search';
    if (path === '/cart') return 'cart';
    if (/^\/blogs\/[^/]+\/[^/]+$/.test(path)) return 'article';
    if (/^\/blogs\/[^/]+$/.test(path)) return 'press';
    const page = path.match(/^\/pages\/([^/]+)$/);
    if (page) {
      const handle = page[1];
      if (/^\d{3}$/.test(handle) && Number(handle) > 0) return 'archive';
      if (handle === 'about' || handle === 'the-brand') return 'brand';
      if (handle === 'locations' || handle === 'stockists') return 'stockists';
      if (handle === 'contact') return 'contact';
      return 'page';
    }
    return null;
  };

  /** Fill in the parts that depend on state: cart rows match the header count, repeated lists get their copies. */
  const prepare = (kind) => {
    if (kind === 'cart') {
      const count = parseInt(document.querySelector('[data-cart-count]')?.textContent, 10) || 0;
      if (count) { host.querySelector('[data-skeleton-cart-empty]')?.remove(); host.querySelector('[data-repeat]').dataset.repeat = Math.min(count, 4); }
      else host.querySelector('[data-skeleton-cart-items]')?.remove();
    }
    host.querySelectorAll('[data-repeat]').forEach((list) => {
      const item = list.firstElementChild;
      for (let i = Number(list.dataset.repeat) - 1; i > 0; i -= 1) list.append(item.cloneNode(true));
    });
  };

  const DELAY = 0; // ms the response may take before the skeleton appears; 0 draws it right away
  let armed = null; // kind waiting for DELAY to pass
  let shown = null; // kind on screen
  let timer;
  const draw = (kind) => { host.replaceChildren(templates[kind].content.cloneNode(true)); prepare(kind); host.classList.remove('is-leaving'); host.hidden = false; };
  const hide = () => {
    clearTimeout(timer);
    armed = null;
    if (!shown) return;
    shown = null;
    host.hidden = true;
    host.classList.remove('is-leaving');
    host.replaceChildren();
    root.classList.remove('is-skeleton');
  };
  const reveal = (kind) => {
    armed = null;
    shown = kind;
    draw(kind);
    root.classList.add('is-skeleton');
    clearTimeout(timer);
    timer = setTimeout(hide, 15000); // the navigation was stopped without telling us: give the page back
  };
  /**
   * Show the skeleton for a destination: a URL string or a link element (its data-skeleton hint wins) — right away, or
   * once DELAY has passed without the new page committing. Returns true when the destination has one, so callers can
   * skip their own leaving effects.
   */
  const show = (target, hint) => {
    if (window.Shopify?.designMode) return false;
    let url;
    try { url = new URL(typeof target === 'string' ? target : target.href, location.href); } catch (e) { return false; }
    if (url.origin !== location.origin) return false;
    const kind = kindFor(url, hint || (target instanceof Element ? target.dataset.skeleton : undefined));
    if (!kind) return false;
    if (shown === kind || armed?.kind === kind) return true;
    clearTimeout(timer);
    if (!DELAY || shown) { reveal(kind); return true; } // (another destination while one is up swaps right away)
    timer = setTimeout(() => reveal(kind), DELAY);
    armed = { kind };
    return true;
  };

  // Plain link clicks (the drawers call show() themselves, before their close plays)
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a[href]');
    if (a) {
      if (a.target === '_blank' || a.hasAttribute('download') || a.origin !== location.origin) return;
      if (a.pathname === location.pathname && a.search === location.search && a.hash) return; // in-page anchor
      show(a);
      return;
    }
    const row = e.target.closest('.search-result[data-url]'); // search rows open on click anywhere (assets/search.js)
    if (row) show(row.dataset.url);
  });
  // GET forms (search) navigate too
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (e.defaultPrevented || form.method !== 'get' || form.target === '_blank') return;
    show(form.action);
  });
  // Navigation API (Chrome, Safari 26): covers scripted navigations too, and takes the skeleton down when one is stopped
  if (window.navigation) {
    navigation.addEventListener('navigate', (e) => {
      if (e.destination.sameDocument || e.downloadRequest !== null || e.formData || (e.navigationType !== 'push' && e.navigationType !== 'replace')) return;
      show(e.destination.url);
    });
    navigation.addEventListener('navigateerror', hide);
  }
  // Tell the next page which skeleton it arrives over (layout/theme.liquid reads it at pagereveal, and so does the hold below)
  window.addEventListener('pagehide', () => {
    if (!shown) return;
    try { sessionStorage.setItem('cjr-skeleton', shown); } catch (err) {}
  });
  // Coming back from the browser cache the page is restored as left: take the skeleton down before it is shown
  window.addEventListener('pagereveal', hide);
  window.addEventListener('pageshow', (e) => { if (e.persisted) hide(); });

  /* Arrival without a view transition: redraw the skeleton the previous page showed and let it go once the entrance
     starts (html.enter), so the page never cuts to blank. Decided at pagereveal, whichever side of it this script runs. */
  let pending = null;
  try { pending = sessionStorage.getItem('cjr-skeleton'); } catch (err) {}
  if (pending && 'onpagereveal' in window) {
    const hold = () => {
      if (!templates[pending] || shown) return;
      shown = pending;
      root.classList.add('from-skeleton'); // pieces fade in over it instead of rising (critical.css)
      draw(pending);
      const watch = new MutationObserver(() => { if (root.classList.contains('enter')) release(); });
      const release = () => {
        watch.disconnect();
        clearTimeout(timer);
        host.classList.add('is-leaving');
        timer = setTimeout(hide, 500);
      };
      if (root.classList.contains('enter')) { release(); return; }
      watch.observe(root, { attributes: true, attributeFilter: ['class'] });
      timer = setTimeout(release, 1500); // the entrance waits for fonts at most 700ms
    };
    const decide = (transitioned) => {
      try { sessionStorage.removeItem('cjr-skeleton'); } catch (err) {}
      if (!transitioned) hold();
    };
    const revealed = ['enter', 'pre-enter', 'vt-active', 'enter-late'].some((c) => root.classList.contains(c));
    if (revealed) decide(root.classList.contains('vt-active') || root.classList.contains('enter-late'));
    else window.addEventListener('pagereveal', (e) => decide(!!e.viewTransition), { once: true });
  }

  window.CJR = window.CJR || {};
  window.CJR.skeleton = { show, hide, kindFor };
})();
